import dayjs from "dayjs"
import { UserActivityState } from "../../../pages/logbook/pages/main/components/activityRegister/mapLeft/types"
import { MarkPoint, UserMarkMap } from "../../../pages/logbook/pages/main/components/shared/map/types"
import { OneUserItem } from "../../../pages/logbook/pages/main/utils/types/activity"
import { Constants } from "../../../utils/constants"
import { getLocalStorage } from "../../../utils/crud/crypt"
import { Roles, Status } from "../../../utils/enums"
import { DestinationsListResponse, DetailLogbook, Item, ItemOneUser, Logbook, LogbookUser, LogbookUserOneUser, MyTypes, User } from "../response"
import { StoreType } from "../../../pages/logbook/pages/main/utils/enums/storeType"
import { ActivityType } from "../../../pages/logbook/pages/main/utils/enums/activityType"
import { formatGeneralEvidenceConfig } from "../../../components/uploadEvidence/methods"
import { PointType } from "../../../pages/logbook/pages/main/utils/enums/pointType"
import { geoDistanceInMeters } from "../../../utils/methods/calcDistanceFromLatAndLong"
import { DestinationProps } from "../../../pages/logbook/pages/main/components/destinationRegistered/components/destination/types"

export const formatUserActivities = ({ response }: { response: LogbookUser }) => {
    const userMarkFilter = response?.items?.filter(logbook => !!logbook?.logbook?.some(logbook => !!logbook?.locationInitActivity || !!logbook?.locationFinishActivity))
    return {
        data: response,
        transform: {
            userList: response.items?.map(user => {
                const statusCountMap = user?.logbook?.reduce((acc: Record<Status, number>, item) => {
                    const type = item.status?.id ?? item.statusId
                    if (type && Object.values(Status).includes(type)) {
                        acc[type] = (acc[type] || 0) + 1
                    }
                    return acc
                }, {} as Record<Status, number>) ?? {}

                return {
                    amount: user.totalLogs ?? 0,
                    completedLogs: user.completedLogs ?? 0,
                    pendingLogns: user.pendingLogs ?? 0,
                    area: user.userDepartments?.map(area => area.name),
                    department: user.userDepartments?.flatMap(area => area.departaments?.map(department => department.name)),
                    curp: user.curp,
                    division: user.division?.description,
                    id: user.id?.toString(),
                    role: user.role?.description,
                    userName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
                    userType: user.userType?.name,
                    activityList: formatActivitiesList({ logbook: user.logbook, userId: user?.id }) ,
                    reportUrl: null,
                    types: [
                        Status.CANCEL,
                        Status.FINISHED,
                        Status.NOT_FINISHED,
                        Status.EXPIRED,
                        Status.STARTED,
                        Status.TO_BE_STARTED
                    ].map((status) => ({
                        count: statusCountMap[status] ?? 0,
                        type: status
                    }))
                }
            }) as OneUserItem[],
            users: /* userMarkFilter */response?.items?.flatMap((user, index) => {
                return user.logbook?.map((log, indexActivity) => {
                    return {
                        user: {
                            userId: user.id?.toString() ?? '',
                            id: `${user.id?.toString()}-${log.id}`
                        },
                    };
                }) ?? [];
            }),
            userMark: /* userMarkFilter */response?.items?.flatMap((user, index) => {
                return user.logbook?.map((log, indexActivity) => {
                    const detail = {
                        title: log.title ?? '',
                        startDate: log.dateStartExecution ? dayjs.utc(log.dateStartExecution).format(Constants.dateFormatDateTimePicker) : '',
                        endDate: log.dateEndExecution ? dayjs.utc(log.dateEndExecution).format(Constants.dateFormatDateTimePicker) : '',
                        startProgramed: log.dateStart ? dayjs.utc(log.dateStart).format(Constants.dateFormatDateTimePicker) : '',
                        endProgramed: log.dateEnd ? dayjs.utc(log.dateEnd).format(Constants.dateFormatDateTimePicker) : '',
                        status: log.statusId,
                        storeAndId: log.storeInit ? `${log?.storeInit?.keyCode} - ${log?.storeInit?.name}` : log.locationInitSiteName ?? '',
                        unityLocation: log.locationInitSite ? log.locationInitSite : `${log.storeInit?.latitude},${log.storeInit?.longitude}`,
                        isForceClose: !!log.forceClose,
                        initLocation: log.locationInitActivity ?? '',
                        finishLocation: log.locationFinishActivity ? log.locationFinishActivity : log.locationFinishSite ?? '',
                        isFree: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                        userName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                    };

                    let storeCoordinates: { lat?: number; lng?: number; storeName?: string; storeType?: StoreType, status?: Status } | undefined = undefined;

                    return {
                        user: {
                            userId: user.id?.toString() ?? '',
                            id: `${user.id?.toString()}-${log.id}`
                        },
                        color: log.statusId ?? Status.TO_BE_STARTED,
                        points: formatActivitiesPoint(log, detail, indexActivity, user) /* coordinates */,
                        storeLocation: storeCoordinates,
                        index: indexActivity + 1
                    };
                }) ?? [];
            }) as UserMarkMap[]

        },
        total: response.totalPages
    }
}

export const formatListTypes = ({ response }: { response: MyTypes }): { types: { count: number, type: Status }[]; total: number } => {
    const total = response.totalItems
    const types = [
        {
            count: response.cancelled,
            type: Status.CANCEL
        },
        {
            count: response.completed,
            type: Status.FINISHED
        },
        {
            count: response.notFinalized,
            type: Status.NOT_FINISHED
        },
        {
            count: response.expired,
            type: Status.EXPIRED
        },
        {
            count: response.started,
            type: Status.STARTED
        },
        {
            count: response.toBeStarted,
            type: Status.TO_BE_STARTED
        }
    ]
    return {
        types,
        total
    }
}

export const formarListOfActivities = ({ response, onlyUser, myActivity }: { response?: LogbookUser | LogbookUserOneUser; onlyUser?: boolean; myActivity?: boolean }) => {
    const firstUser = (response as LogbookUser)?.items?.[0]
    const statusCountMap = firstUser?.logbook?.reduce((acc: Record<Status, number>, item) => {
        const type = item.status?.id ?? item.statusId;
        if (type && type in Status) {
            acc[type] = (acc[type] || 0) + 1;
        }
        return acc;
    }, {} as Record<Status, number>) ?? {};

    const types = Object.values([Status.CANCEL, Status.FINISHED, Status.NOT_FINISHED, Status.EXPIRED, Status.STARTED, Status.TO_BE_STARTED]).map((status) => ({
        count: statusCountMap[status] ?? 0,
        type: status
    }));

    return {
        data: response,
        total: response?.totalPages,
        transform: {
            ...!myActivity && {
                ...!!onlyUser && {
                    oneUser: {
                        id: firstUser?.id,
                        userName: `${firstUser?.firstName} ${firstUser?.lastName}`,
                        curp: firstUser?.curp,
                        activityList: formatActivitiesList({ logbook: firstUser?.logbook, userId: firstUser?.id }),
                        types: types
                    }
                },
                ...!onlyUser && {
                    userList: (response as LogbookUser)?.items?.map(user => ({
                        id: user.id,
                        user: {
                            curp: user.curp,
                            userName: `${user.firstName} ${user.lastName}`,
                            amount: user.totalLogs,
                            completedLogs: user.completedLogs
                        },
                        activityList: formatActivitiesList({ logbook: user.logbook, userId: user.id })
                    }))
                },
            },
            ...!!myActivity && {
                myActivity: {
                    types: [],
                    amount: response?.totalItems,
                    activityList: formatMyActivitiesList({ logbook: (response as LogbookUserOneUser).items })
                }
            },

            ...!myActivity && {
                userMark: (response as LogbookUser)?.items?.flatMap((logbook, index) => {
                    const activityPoints: UserMarkMap[] = []
                    logbook.logbook?.forEach((activity, activityIndex) => {
                        let storeCoordinates: { lat?: number; lng?: number; storeName?: string; storeType?: StoreType, status?: Status } | undefined = undefined;
                        const detail = {
                            title: activity.title ?? '',
                            startDate: activity.dateStartExecution ? dayjs.utc(activity.dateStartExecution).format(Constants.dateFormatDateTimePicker) : '',
                            endDate: activity.dateEndExecution ? dayjs.utc(activity.dateEndExecution).format(Constants.dateFormatDateTimePicker) : '',
                            startProgramed: activity.dateStart ? dayjs.utc(activity.dateStart).format(Constants.dateFormatDateTimePicker) : '',
                            endProgramed: activity.dateEnd ? dayjs.utc(activity.dateEnd).format(Constants.dateFormatDateTimePicker) : '',
                            status: activity.statusId,
                            storeAndId: activity.storeInit ? `${activity?.storeInit?.keyCode} - ${activity?.storeInit?.name}` : activity.locationInitSiteName ?? '',
                            unityLocation: activity.locationInitSite ? activity.locationInitSite : `${activity.storeInit?.latitude},${activity.storeInit?.longitude}`,
                            isForceClose: !!activity.forceClose,
                            initLocation: activity.locationInitActivity ?? '',
                            finishLocation: activity.locationFinishActivity ? activity.locationFinishActivity : activity.locationFinishSite ?? '',
                            isFree: activity.geofence ? ActivityType.AUTOMATIC : !activity.dateEnd && !activity.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            userName: `${logbook.firstName ?? ''} ${logbook.lastName ?? ''}`.trim()
                        };

                        activityPoints.push({
                            user: {
                                userId: logbook.id?.toString() ?? "",
                                id: `${logbook.id?.toString()}-${activity.id}`
                            },
                            color: activity.statusId ?? Status.TO_BE_STARTED,
                            points: formatActivitiesPoint(activity, detail, activityIndex, logbook)/* coordinates as MarkPoint[] */,
                            storeLocation: storeCoordinates,
                            index: activityIndex + 1
                        })
                    })
                    return activityPoints
                })
            },


            ...!!myActivity && {
                userMark: (response as LogbookUserOneUser)?.items?.flatMap((logbook, index) => {
                    const activityPoints: UserMarkMap[] = []
                    let storeCoordinates: { lat?: number; lng?: number; storeName?: string; storeType?: StoreType, status?: Status } | undefined = undefined;
                    const detail = {
                        title: logbook.title ?? '',
                        startDate: logbook.dateStartExecution ? dayjs.utc(logbook.dateStartExecution).format(Constants.dateFormatDateTimePicker) : '',
                        endDate: logbook.dateEndExecution ? dayjs.utc(logbook.dateEndExecution).format(Constants.dateFormatDateTimePicker) : '',
                        startProgramed: logbook.dateStart ? dayjs.utc(logbook.dateStart).format(Constants.dateFormatDateTimePicker) : '',
                        endProgramed: logbook.dateEnd ? dayjs.utc(logbook.dateEnd).format(Constants.dateFormatDateTimePicker) : '',
                        status: logbook.statusId,
                        storeAndId: logbook.storeInit ? `${logbook?.storeInit?.keyCode} - ${logbook?.storeInit?.name}` : logbook.locationInitSiteName ?? '',
                        unityLocation: logbook.locationInitSite ? logbook.locationInitSite : `${logbook.storeInit?.latitude},${logbook.storeInit?.longitude}`,
                        isForceClose: !!logbook.forceClose,
                        initLocation: logbook.locationInitActivity ?? '',
                        finishLocation: logbook.locationFinishActivity ? logbook.locationFinishActivity : logbook.locationFinishSite ?? '',
                        isFree: logbook.geofence ? ActivityType.AUTOMATIC : !logbook.dateEnd && !logbook.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                        userName: `${logbook.user?.firstName} ${logbook.user?.lastName}`
                    };

                    activityPoints.push({
                        user: {
                            userId: logbook.userId?.toString() ?? "",
                            id: `${logbook.userId?.toString()}-${logbook.id}`
                        },
                        color: logbook.statusId ?? Status.TO_BE_STARTED,
                        points: formatActivitiesPoint(logbook, detail, index, logbook.user)/* coordinates as MarkPoint[] */,
                        storeLocation: storeCoordinates,
                        index: index + 1
                    })
                    return activityPoints
                })
            }
        } as UserActivityState & { userMark: UserMarkMap[] },
    }
}

export const formarListOfDestinations = ({ response }: { response: DestinationsListResponse }) => {
    const list: DestinationProps[] = response.items?.map(destination => ({
      id: destination.id,
      companyName: destination.companyName,
      createdDate: dayjs(destination.createTime).format(Constants.dayjsFormat),
      direction: destination.address,
      latitudeAndLongitude: `${destination.latitude}, ${destination.longitude}`,
      name: destination.name,
      userCreator: destination.fullName,
      companyId: destination.companyId,
      storeId: destination.storeId
    })) ?? [];

    return {
        data: list,
        total: response?.totalPages
    }
}

const formatActivitiesPoint = (log: Logbook | ItemOneUser, detail: DetailLogbook, indexActivity: number, user: Item | User) => {
    const coordinates: MarkPoint[] = [];
    const digitalFenceFixed = log.digitalFenceFixed ? log.digitalFenceFixed : log.digitalFenceMts;
    if (log.geofence) {
        if (log.storeInit && !log.locationInitActivity && !log.locationFinishActivity && !log.locationFinishSite) {
            //Si hay unidad no hay inicio ni hay fin
            coordinates.push({
                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                lat: Number(log.storeInit.latitude),
                lng: Number(log.storeInit.longitude),
                extraData: `${(indexActivity + 1)}`,
                label: 'Inicio de la actividad',
                //color:getStatus(answer?.statusId as Status, colors, t),
                type: PointType.UNITY,
                typeCopy: PointType.UNITY,
                isUnity: !!log.storeInit,
                isDetination:!!log.storeInit,
                count: 0,
                isCenter: false,
                statusPoint: log.statusId as Status,
                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                ...detail
            });
        }

        if (log.storeInit && log.locationInitActivity && !log.locationFinishActivity && !log.locationFinishSite) {
            //Si hay unidad, hay inicio pero no hay fin hay fin
            //const [latInit, lngInit] = log.locationInitActivity.split(',').map(Number);
            /* const meters = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInit, lng: lngInit }, to: { lat: Number(log.storeInit.latitude), lng: Number(log.storeInit.longitude) } })) ?? 0)
            const intoRange = log.digitalFenceFixed ?? 0 >= meters */

            //El inicio esta dentro del rango
            coordinates.push({
                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                lat: Number(log.storeInit.latitude),
                lng: Number(log.storeInit.longitude),
                extraData: `${(indexActivity + 1)}`,
                label: 'Inicio de la actividad',
                //color:getStatus(answer?.statusId as Status, colors, t),
                type: PointType.UNITY_START,
                typeCopy: PointType.UNITY_START,
                isUnity: !!log.storeInit,
                isDetination:!!log.storeInit,
                count: 0,
                isCenter: false,
                statusPoint: log.statusId as Status,
                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                ...detail
            });
        }

        if (log.storeInit && log.locationInitActivity && (log.locationFinishActivity || log.locationFinishSite)) {
            //Si hay unidad, hay inicio y hay fin
            //const [latInit, lngInit] = log.locationInitActivity.split(',').map(Number);
            const [latFinish, lngFinish] = log.locationFinishActivity ? log.locationFinishActivity.split(',').map(Number) : log.locationFinishSite ? log.locationFinishSite?.split(',').map(Number) : [0, 0];
            const meters = Math.abs(Number(geoDistanceInMeters({ from: { lat: latFinish, lng: lngFinish }, to: { lat: Number(log.storeInit.latitude), lng: Number(log.storeInit.longitude) } })) ?? 0)
            const intoRangeEnd = !!(digitalFenceFixed >= meters)

            if (intoRangeEnd) {
                //Si el punto de fin esta dentro del cercado
                coordinates.push({
                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                    lat: Number(log.storeInit.latitude),
                    lng: Number(log.storeInit.longitude),
                    extraData: `${(indexActivity + 1)}`,
                    label: 'Fin de la actividad',
                    //color:colors.color19,
                    type: PointType.UNITYT_START_END,
                    typeCopy: PointType.UNITYT_START_END,
                    isUnity: !!log.storeInit,
                    isDetination:!!log.storeInit,
                    count: 0,
                    isCenter: false,
                    statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status /* log.statusId as Status */,
                    userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                    ...detail
                });
            } else {
                //Si hel punto fin no esta en el cercado se pintan 2 puntos
                coordinates.push({
                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                    lat: Number(log.storeInit.latitude),
                    lng: Number(log.storeInit.longitude),
                    extraData: `${(indexActivity + 1)}.1`,
                    label: 'Fin de la actividad',
                    //color:colors.color19,
                    type: PointType.UNITY_START,
                    typeCopy: PointType.UNITY_START,
                    isUnity: !!log.storeInit,
                    isDetination:!!log.storeInit,
                    count: 0,
                    isCenter: false,
                    statusPoint: log.statusId as Status,
                    userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                    ...detail
                });
                coordinates.push({
                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                    lat: latFinish,
                    lng: lngFinish,
                    extraData: `${(indexActivity + 1)}.2`,
                    label: 'Inicio de la actividad',
                    type: PointType.END,
                    typeCopy: PointType.END,
                    isUnity: !!log.storeInit,
                    isDetination:!!log.storeInit,
                    count: 0,
                    isCenter: false,
                    statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status,
                    userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                    ...detail
                });
            }
        }
    } else {
        //Si no tiene geofence
        //Si es un destino o una unidad
        if (log.locationInitSite || log.storeInit) {
            const [latInit, lngInit] = log.locationInitSite ? log.locationInitSite.split(',').map(Number) : [Number(log.storeInit?.latitude), Number(log.storeInit?.longitude)];
            //Si la ubicacion es diferente de 0 se muestra el punto del estino
            if (latInit !== 0 || lngInit !== 0) {
                //Solo esta la unidad
                if (!log.locationInitActivity && !log.locationFinishActivity && !log.locationFinishSite) {
                    coordinates.push({
                        typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                        lat: latInit,
                        lng: lngInit,
                        extraData: `${indexActivity + 1}`,
                        label: 'Inicio de la actividad',
                        type: PointType.UNITY, //aca
                        typeCopy: PointType.UNITY,
                        isUnity: true/* !!log.storeInit */,
                        isDetination:!!log.storeInit,
                        count: 0,
                        isCenter: false,
                        statusPoint: log.statusId as Status,
                        userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                        ...detail
                    });
                }

                //Existe unidad y un inicio
                if (log.locationInitActivity && !log.locationFinishActivity && !log.locationFinishSite) {
                    const [latInitStart, lngInitStart] = log.locationInitActivity.split(',').map(Number);
                    const meters = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInitStart, lng: lngInitStart }, to: { lat: latInit, lng: lngInit } })) ?? 0)
                    const intoRangeStart = !!(digitalFenceFixed >= meters)

                    if (intoRangeStart) {
                        //El punto de inicio esta dentro del rango
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITY_START, //aca
                            typeCopy: PointType.UNITY_START,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    } else {
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}.1`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITY, //aca
                            typeCopy: PointType.UNITY,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInitStart,
                            lng: lngInitStart,
                            extraData: `${indexActivity + 1}.2`,
                            label: 'Inicio de la actividad',
                            type: PointType.START, //aca
                            typeCopy: PointType.START,
                            isUnity: !!log.storeInit,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    }
                }

                //Existe un inicio y un fin
                if (log.locationInitActivity && (log.locationFinishActivity || log.locationFinishSite)) {

                    let finishLocation = '0,0';
                    if (log.locationFinishSite) {
                        finishLocation = log.locationFinishSite;
                    }
                    else if (log.locationFinishActivity) {
                        finishLocation = log.locationFinishActivity;
                    }

                    const [latInitStart, lngInitStart] = log.locationInitActivity.split(',').map(Number);
                    const metersStart = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInitStart, lng: lngInitStart }, to: { lat: latInit, lng: lngInit } })) ?? 0)
                    const intoRangeStart = !!(digitalFenceFixed >= metersStart)

                    const [latInitEnd, lngInitEnd] = finishLocation.split(',').map(Number);
                    const metersEnd = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInitEnd, lng: lngInitEnd }, to: { lat: latInit, lng: lngInit } })) ?? 0)
                    const intoRangeEnd = !!(digitalFenceFixed >= metersEnd)

                    const metersEndToStart = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInitEnd, lng: lngInitEnd }, to: { lat: latInitStart, lng: lngInitStart } })) ?? 0)
                    const intoRangeEndIntoStart = !!(digitalFenceFixed >= metersEndToStart)
                    if (intoRangeStart && intoRangeEnd) {
                        //Todos los puntos estan en el rango se pinta 1
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITYT_START_END, //aca
                            typeCopy: PointType.UNITYT_START_END,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status /* log.statusId as Status */,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    }
                    if (intoRangeStart && !intoRangeEnd) {
                        //Solo el inicio esta en el rango se pintan 2
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}.1`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITY_START, //aca
                            typeCopy: PointType.UNITY_START,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInitEnd,
                            lng: lngInitEnd,
                            extraData: `${indexActivity + 1}.2`,
                            label: 'Inicio de la actividad',
                            type: PointType.END, //aca
                            typeCopy: PointType.END,
                            isUnity: !!log.storeInit,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    }
                    if (!intoRangeStart && intoRangeEnd) {
                        //Solo el fin esta en el rango se pintan 2
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInitStart,
                            lng: lngInitStart,
                            extraData: `${indexActivity + 1}.1`,
                            label: 'Inicio de la actividad',
                            type: PointType.START, //aca
                            typeCopy: PointType.START,
                            isUnity: !!log.storeInit,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}.2`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITY_END, //aca
                            typeCopy: PointType.UNITY_END,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status /* log.statusId as Status */,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    }
                    if (!intoRangeStart && !intoRangeEnd) {
                        //Ni el inicio ni el fin esta en el rango se pintan 3
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInit,
                            lng: lngInit,
                            extraData: `${indexActivity + 1}.1`,
                            label: 'Inicio de la actividad',
                            type: PointType.UNITY, //aca
                            typeCopy: PointType.UNITY,
                            isUnity: true/* !!log.storeInit */,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: false,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                        if ( ( latInitStart == latInitEnd && lngInitStart == lngInitEnd ) || intoRangeEndIntoStart) { //tienen las mismas coordenandas o el fin esta dentro del rango del inicio
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitStart,
                                lng: lngInitStart,
                                extraData: `${indexActivity + 1}.2`,
                                label: 'Inicio de la actividad',
                                type: PointType.START_END, //aca
                                typeCopy: PointType.START_END,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: false,
                                statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status /* log.statusId as Status */,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                        } else {
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitStart,
                                lng: lngInitStart,
                                extraData: `${indexActivity + 1}.2`,
                                label: 'Inicio de la actividad',
                                type: PointType.START, //aca
                                typeCopy: PointType.START,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: false,
                                statusPoint: log.statusId as Status,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitEnd,
                                lng: lngInitEnd,
                                extraData: `${indexActivity + 1}.3`,
                                label: 'Inicio de la actividad',
                                type: PointType.END, //aca
                                typeCopy: PointType.END,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: false,
                                statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                        }
                    }
                }
            } else {
                //Si la ubicacion es 0 se debe mostrar el punto de inicio y fin
                //Se muestra el incio y el fin
                let finishLocation = null;
                if (/* log.forceClose &&  */log.locationFinishSite) {
                    finishLocation = log.locationFinishSite;
                }
                else if (/* !log.forceClose && */ log.locationFinishActivity) {
                    finishLocation = log.locationFinishActivity;
                }

                if (log.locationInitActivity) {
                    const [latInitStart, lngInitStart] = log.locationInitActivity.split(',').map(Number);
                    const [latInitEnd, lngInitEnd] = finishLocation?.split(',').map(Number) ?? [0,0];
                    const metersEndToStart = Math.abs(Number(geoDistanceInMeters({ from: { lat: latInitEnd, lng: lngInitEnd }, to: { lat: latInitStart, lng: lngInitStart } })) ?? 0)
                    const intoRangeEndIntoStart = !!(digitalFenceFixed >= metersEndToStart)
                    if (finishLocation == null) {
                        //Mostrar solo punto de inicio
                        coordinates.push({
                            typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                            lat: latInitStart,
                            lng: lngInitStart,
                            extraData: `${indexActivity + 1}`,
                            label: 'Inicio de la actividad',
                            type: PointType.START,
                            typeCopy: PointType.START,
                            isUnity: !!log.storeInit,
                            isDetination:!!log.storeInit,
                            count: 0,
                            isCenter: true,
                            statusPoint: log.statusId as Status,
                            userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                            ...detail
                        });
                    } else {
                        const [latInitEnd, lngInitEnd] = finishLocation.split(',').map(Number);
                        //Mostrar punto de inicio y fin
                        if (latInitStart !== latInitEnd && lngInitStart !== lngInitEnd && !intoRangeEndIntoStart) { //Si no son las mismas coordenadas o la ubicacion de fin esta fuera del rango de inicio
                            //El punto de inicio y fin tienen coordenadas diferentes se muestran ambos
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitStart,
                                lng: lngInitStart,
                                extraData: `${indexActivity + 1}.1`,
                                label: 'Inicio de la actividad',
                                type: PointType.START,
                                typeCopy: PointType.START,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: true,
                                statusPoint: log.statusId as Status,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitEnd,
                                lng: lngInitEnd,
                                extraData: `${indexActivity + 1}.2`,
                                label: 'Inicio de la actividad',
                                type: PointType.END,
                                typeCopy: PointType.END,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: true,
                                statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                        } else {
                            //El puntoi de inicio y fin tienen las mismas coordenadas se muestra 1 solo
                            coordinates.push({
                                typeActivity: log.geofence ? ActivityType.AUTOMATIC : !log.dateEnd && !log.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
                                lat: latInitStart,
                                lng: lngInitStart,
                                extraData: `${indexActivity + 1}`,
                                label: 'Inicio de la actividad',
                                type: PointType.START_END,
                                typeCopy: PointType.START_END,
                                isUnity: !!log.storeInit,
                                isDetination:!!log.storeInit,
                                count: 0,
                                isCenter: true,
                                statusPoint: log.forceClose ? Status.EXPIRED : log.statusId as Status /* log.statusId as Status */,
                                userData: `${(user.firstName ?? '').slice(0, 2)}${(user.lastName ?? '').slice(0, 2)}`.toUpperCase().trim(),
                                ...detail
                            });
                        }
                    }
                }
            }
        }
    }

    return coordinates;
}

const formatActivitiesList = ({ logbook, userId }: { logbook?: Logbook[]; userId?: string | number }) => {
    const state = getLocalStorage<any>()
    return logbook?.map((logbook, index) => ({
        closeStore: logbook.storeFinish ? `${logbook?.storeFinish?.keyCode} - ${logbook?.storeFinish?.name}` : logbook.locationFinishSiteName,
        id: `${userId}-${logbook.id?.toString()}`,
        closeMotive: logbook.closeMotive,
        position: index + 1,
        locationInitSiteName: logbook.locationInitSiteName ?? '',
        timeZone: logbook.storeInit?.storeTimeZone ?? Constants.defaultTimeZoneObject,
        unityLocation: logbook.locationInitSite ? logbook.locationInitSite : `${logbook.storeInit?.latitude},${logbook.storeInit?.longitude}`,
        //initDate: logbook.dateStart ? dayjs(logbook.dateStart).add(logbook.offsetSite ?? 0, 'minute').format(Constants.dayjsFormat) : '',
        //endDate: logbook.dateEnd ? dayjs(logbook.dateEnd).add(logbook.offsetSite ?? 0, 'minute').format(Constants.dayjsFormat) : '',
        initDate: logbook.dateStart ? dayjs.utc(logbook.dateStart).format(Constants.dayjsFormat) : '',
        endDate: logbook.dateEnd ? dayjs.utc(logbook.dateEnd).format(Constants.dayjsFormat) : '',
        startDateJs: logbook.dateStart ? dayjs.utc(logbook.dateStart) : null,
        endDateJs: logbook.dateEnd ? dayjs.utc(logbook.dateEnd) : null,
        storeAndId: logbook.storeInit ? `${logbook?.storeInit?.keyCode} - ${logbook?.storeInit?.name}` : logbook.locationInitSiteName,
        typeActivity: logbook.geofence ? ActivityType.AUTOMATIC : !logbook.dateEnd && !logbook.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
        title: logbook.title,
        endExecution: logbook.dateEndExecution ? dayjs.utc(logbook.dateEndExecution).format(Constants.dayjsFormat) : 'N/A',
        startExecution: logbook.dateStartExecution ? dayjs.utc(logbook.dateStartExecution).format(Constants.dayjsFormat) : 'N/A',
        initLocation: logbook.locationInitActivity,
        finishLocation: logbook.locationFinishActivity,
        type: logbook.typeActivity?.title,
        isForcedClosure: !!logbook.forceClose,
        status: logbook.statusId,
        reportUrl: logbook.reportUrl,
        evidenceConfig: formatGeneralEvidenceConfig({ config: logbook.currentTypeActivity?.configEvidences, evidences: logbook.evidences }),
        evidences: logbook.evidences,
        assignedBy: `${logbook.userManage?.firstName ?? ""} ${logbook.userManage?.lastName ?? ''}`.trim(),
        isUserWithPermission: (userId == state.id) || logbook.canDoActions || state.role == Roles.ADMIN,
        isTheUserAssigned: logbook?.userId == state.id || state.role == Roles.ADMIN /* logbook?.userManage?.id == state.id || state.role == Roles.ADMIN */,
        geofence: !!logbook?.geofence,
        scheduleVisitId: logbook.scheduledVisitId,
        model: logbook.model,
        modelId: logbook.modelId,
        //isMyActivity: state.id == logbook.userManageId
    }))
}

const formatMyActivitiesList = ({ logbook }: { logbook?: ItemOneUser[]; }) => {
    const state = getLocalStorage<any>()
    return logbook?.map((logbook, index) => ({
        closeStore: logbook.storeFinish ? `${logbook?.storeFinish?.keyCode} - ${logbook?.storeFinish?.name}` : logbook.locationFinishSiteName,
        id: `${logbook.userId}-${logbook.id?.toString()}`,
        closeMotive: logbook.closeMotive,
        position: index + 1,
        locationInitSiteName: logbook.locationInitSiteName ?? '',
        timeZone: logbook.storeInit?.storeTimeZone ?? Constants.defaultTimeZoneObject,
        unityLocation: logbook.locationInitSite ? logbook.locationInitSite : `${logbook.storeInit?.latitude},${logbook.storeInit?.longitude}`,
        //initDate: logbook.dateStart ? dayjs(logbook.dateStart).add(logbook.offsetSite ?? 0, 'minute').format(Constants.dayjsFormat) : '',
        //endDate: logbook.dateEnd ? dayjs(logbook.dateEnd).add(logbook.offsetSite ?? 0, 'minute').format(Constants.dayjsFormat) : '',
        initDate: logbook.dateStart ? dayjs.utc(logbook.dateStart).format(Constants.dayjsFormat) : '',
        endDate: logbook.dateEnd ? dayjs.utc(logbook.dateEnd).format(Constants.dayjsFormat) : '',
        startDateJs: logbook.dateStart ? dayjs.utc(logbook.dateStart) : null,
        endDateJs: logbook.dateEnd ? dayjs.utc(logbook.dateEnd) : null,
        storeAndId: logbook.storeInit ? `${logbook?.storeInit?.keyCode} - ${logbook?.storeInit?.name}` : logbook.locationInitSiteName,
        title: logbook.title,
        initLocation: logbook.locationInitActivity,
        finishLocation: logbook.locationFinishActivity,
        endExecution: logbook.dateEndExecution ? dayjs.utc(logbook.dateEndExecution).format(Constants.dayjsFormat) : 'N/A',
        startExecution: logbook.dateStartExecution ? dayjs.utc(logbook.dateStartExecution).format(Constants.dayjsFormat) : 'N/A',
        type: logbook.typeActivity?.title,
        isForcedClosure: !!logbook.forceClose,
        typeActivity: logbook.geofence ? ActivityType.AUTOMATIC : !logbook.dateEnd && !logbook.dateStart ? ActivityType.FREE : ActivityType.PROGRAMMED,
        status: logbook.statusId,
        reportUrl: logbook.reportUrl,
        evidenceConfig: formatGeneralEvidenceConfig({ config: logbook.currentTypeActivity?.configEvidences, evidences: logbook.evidences }),
        evidences: logbook.evidences,
        assignedBy: `${logbook.userManage?.firstName ?? ""} ${logbook.userManage?.lastName ?? ''}`.trim(),
        isUserWithPermission: true,
        isTheUserAssigned: logbook?.userId == state.id || state.role == Roles.ADMIN/* logbook?.userManage?.id == state.id || state.role == Roles.ADMIN */,
        geofence: !!logbook?.geofence,
        //isMyActivity: false
    }))
}